/**
 * Incorrect Answer Tracker Service
 * Tracks which questions were answered incorrectly for targeted practice
 */

import { logger } from '../utils/logger';

interface IncorrectAnswer {
  questionId: string;
  testId: string;
  timestamp: string;
  userAnswer: string;
  correctAnswer: string;
}

interface TestIncorrectAnswers {
  testId: string;
  incorrectAnswers: IncorrectAnswer[];
  lastUpdated: string;
}

const STORAGE_KEY = 'incorrectAnswers';

class IncorrectAnswerTrackerService {
  /**
   * Track an incorrect answer
   */
  trackIncorrectAnswer(
    testId: string,
    questionId: string,
    userAnswer: string,
    correctAnswer: string
  ): void {
    try {
      const data = this.getTrackingData();
      
      // Find or create test entry
      let testEntry = data.find(entry => entry.testId === testId);
      if (!testEntry) {
        testEntry = {
          testId,
          incorrectAnswers: [],
          lastUpdated: new Date().toISOString()
        };
        data.push(testEntry);
      }
      
      // Check if this question is already tracked
      const existing = testEntry.incorrectAnswers.find(
        answer => answer.questionId === questionId
      );
      
      if (!existing) {
        // Add new incorrect answer
        testEntry.incorrectAnswers.push({
          questionId,
          testId,
          timestamp: new Date().toISOString(),
          userAnswer,
          correctAnswer
        });
        testEntry.lastUpdated = new Date().toISOString();
        this.saveTrackingData(data);
        logger.debug('Tracked incorrect answer:', { testId, questionId });
      }
    } catch (error) {
      logger.error('Failed to track incorrect answer:', error);
    }
  }

  /**
   * Get all incorrect question IDs for a test
   */
  getIncorrectQuestionIds(testId: string): string[] {
    try {
      const data = this.getTrackingData();
      const testEntry = data.find(entry => entry.testId === testId);
      if (!testEntry) return [];
      
      return testEntry.incorrectAnswers.map(answer => answer.questionId);
    } catch (error) {
      logger.error('Failed to get incorrect question IDs:', error);
      return [];
    }
  }

  /**
   * Get incorrect answers for a test
   */
  getIncorrectAnswers(testId: string): IncorrectAnswer[] {
    try {
      const data = this.getTrackingData();
      const testEntry = data.find(entry => entry.testId === testId);
      if (!testEntry) return [];
      
      return testEntry.incorrectAnswers;
    } catch (error) {
      logger.error('Failed to get incorrect answers:', error);
      return [];
    }
  }

  /**
   * Clear incorrect answers for a test (when user masters it)
   */
  clearIncorrectAnswers(testId: string): void {
    try {
      const data = this.getTrackingData();
      const filtered = data.filter(entry => entry.testId !== testId);
      this.saveTrackingData(filtered);
      logger.debug('Cleared incorrect answers for test:', testId);
    } catch (error) {
      logger.error('Failed to clear incorrect answers:', error);
    }
  }

  /**
   * Clear all tracking data
   */
  clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      logger.debug('Cleared all incorrect answer tracking');
    } catch (error) {
      logger.error('Failed to clear all tracking:', error);
    }
  }

  /**
   * Get tracking data from storage
   */
  private getTrackingData(): TestIncorrectAnswers[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      logger.error('Failed to read tracking data:', error);
      return [];
    }
  }

  /**
   * Save tracking data to storage
   */
  private saveTrackingData(data: TestIncorrectAnswers[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Failed to save tracking data:', error);
    }
  }
}

export const incorrectAnswerTracker = new IncorrectAnswerTrackerService();
